// The historic trend series: one point per calendar month, averaged over the
// runs that landed in that month.
//
// The roll-up is done server-side (see get_run_trend_series.py) so the
// definition of a month's value lives in one place. This hook only fetches.
//
// The series is recorded per run at run completion, NOT computed on demand:
// a trend needs one reading per historic run, and the transforms behind it are
// computed on demand at >8s cold. Reading a year of runs from the page would
// take minutes and evict every other cached transform.
import { useEffect, useState } from "react";
import { usePerspective } from "../../contexts/app/provider";
import { TrendPoint } from "./trend_series";
import {
  GetMyRunTrendSeriesDocument,
  GetMyRunTrendSeriesQuery,
} from "../../services/perspective_api/__generated__/perspective_server-types";

export type TrendSeries = {
  points: TrendPoint[];
  loading: boolean;
  /** True once a fetch has settled, so an empty series can be told apart from
   *  one that has not loaded — they need different messages on screen. */
  loaded: boolean;
  /** The request failed. Distinct from an empty series, which is a fact about
   *  the customer; this is a fact about the request. */
  unavailable: boolean;
};

export const useTrendSeries = (): TrendSeries => {
  const { perspectiveApiService } = usePerspective();
  const [state, setState] = useState<TrendSeries>({
    points: [],
    loading: true,
    loaded: false,
    unavailable: false,
  });

  useEffect(() => {
    let cancelled = false;
    setState({ points: [], loading: true, loaded: false, unavailable: false });
    perspectiveApiService
      .getClient()
      .query({ query: GetMyRunTrendSeriesDocument })
      .then((result) => {
        if (cancelled) {
          return;
        }
        const rows: GetMyRunTrendSeriesQuery["getMyRunTrendSeries"] =
          result.data?.getMyRunTrendSeries ?? [];
        setState({
          points: rows.map((r) => ({
            month: r.month,
            runCount: r.runCount,
            inventoryUnits: r.inventoryUnits ?? null,
            unfulfilledUnits: r.unfulfilledUnits ?? null,
            excessUnits: r.excessUnits ?? null,
            inventoryRunCount: r.inventoryRunCount,
            unfulfilledRunCount: r.unfulfilledRunCount,
            excessRunCount: r.excessRunCount,
          })),
          loading: false,
          loaded: true,
          unavailable: false,
        });
      })
      .catch(() => {
        // NOT the empty case. The resolver already returns [] for a missing
        // series, an unreadable one, and a customer with no runs — so the only
        // thing reaching here is a transport or GraphQL failure. Reporting it
        // as "no months recorded yet" would state a fact about the customer's
        // history on the strength of a request that never arrived.
        if (!cancelled) {
          setState({
            points: [],
            loading: false,
            loaded: true,
            unavailable: true,
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [perspectiveApiService]);

  return state;
};

export type { TrendPoint } from "./trend_series";
export { monthsSpanning } from "./trend_series";
